import { useCallback, useState, type ChangeEvent } from "react";

import { Button, Form, Modal, InputGroup, ProgressBar } from "react-bootstrap";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { api } from "../../utils/api";

import { getUploadFileCustomErrorMessage } from "../../utils/errorMessages";
import type z from "zod";
import { schemas } from "../../api/generated";
import { validateFile } from "../../utils/attackLibraryValidation";

type FileDTO = z.infer<typeof schemas.FileWithUsage>;
const ProblemDetails = schemas.ProblemDetails;

const libraryCipherNote =
  "Replacing an Attack Library file will retain existing user assignments. However, due to current design limitations, the user's passwords and the encryption cipher of the Attack Library file must match for devices to be able download and decrypt them.";

export function ReplaceAttackLibraryModal({
  show,
  setShow,
  originalFile,
  token,
  onFileReplaced,
}: {
  show: boolean;
  // eslint-disable-next-line no-unused-vars
  setShow: (show: boolean) => void;
  originalFile: FileDTO;
  token: string;
  // eslint-disable-next-line no-unused-vars
  onFileReplaced?: (file: FileDTO) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      return;
    }

    const validationError = validateFile(file);

    if (validationError) {
      toast.error(validationError);
      e.target.value = ""; // Reset file input
      setFile(null);
      return;
    }

    setFile(file);
    toast.clearWaitingQueue();
  }, []);

  const handleReplace = useCallback(async () => {
    if (!file) {
      return toast.error("Please select a file to upload");
    }

    setIsUploading(true);

    const uploadTimeout = setInterval(() => {
      setUploadProgress((progress) => Math.min(progress + 10, 100));
    }, 50);

    function cancelUploadDueToError(message: string) {
      toast.error(message);
      clearInterval(uploadTimeout);
      setIsUploading(false);
      setUploadProgress(0);
    }

    try {
      // Find all users who are assigned the existing file
      const assignedUsers = await api.getUsersByFileAssignment(
        token,
        originalFile.fileName
      );
      console.log("Assigned users:", assignedUsers);

      // Rename the old file first incase something fails with the upload or reassignment
      const newFileName = `${originalFile.fileName.replace(/\.7z$/, "")}_replaced_${Date.now()}.7z`;
      await api
        .updateFile(token, originalFile.id!, { fileName: newFileName })
        .catch((err) => {
          throw new Error(
            "Upload cancelled due to failure to rename existing file.",
            { cause: err }
          );
        });
      // }

      // Upload the new file
      const formData = new FormData();
      formData.append("file", file);
      const uploadResponse = await api
        .uploadFile(token, formData)
        .catch((err) => {
          const { success, data } = ProblemDetails.safeParse(err);
          throw success
            ? new Error(getUploadFileCustomErrorMessage(data))
            : err;
        });
      if (uploadResponse.message.toLowerCase().includes("already exist")) {
        throw new Error(
          "Replacement failed because a file with the same name already exists"
        );
      }
      console.log("Uploaded file:", uploadResponse);

      // Assign the new file to the selected user
      await Promise.all(
        assignedUsers.map(async (user) => {
          await api.assignFileToUser(token, {
            userId: user.id,
            fileId: uploadResponse.file?.id!,
          });
          console.log(
            `Assigned file ${uploadResponse.file?.fileName} to user ${user.name}`
          );
        })
      ).catch((err) => {
        throw new Error("Failed to assign the new file to existing users.", {
          cause: err,
        });
      });

      // Delete the old file
      await api.deleteFile(token, originalFile.id!).catch((err) => {
        console.error("Error deleting old file during replacement:", err);
        toast.warn("Failed to delete the old file");
      });

      const newFile = {
        ...uploadResponse.file!, // FUTURE: Change types to better align
        assignmentCount: assignedUsers.length,
      };
      onFileReplaced?.(newFile);
      setShow(false);

      toast.success("Attack Library file replaced successfully");
      clearInterval(uploadTimeout);
      setIsUploading(false);
      setUploadProgress(0);
      setFile(null);
    } catch (err) {
      console.error("Error replacing file:", err);
      cancelUploadDueToError(
        (err instanceof Error ? err.message : String(err)) ||
          "An unexpected error occurred during file replacement."
      );
    }
  }, [token, originalFile, file, onFileReplaced, setShow]);

  return (
    <Modal show={show} onHide={() => setShow(false)}>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>Replace File</Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column gap-3">
        <InputGroup size="sm" style={{ minHeight: "2rem" }}>
          <Form.Control
            placeholder="Select a replacement file"
            type="file"
            accept=".7z"
            multiple={false}
            name="attackLibrary"
            disabled={isUploading}
            onChange={handleChange}
          />
        </InputGroup>

        {isUploading && (
          <ProgressBar
            now={uploadProgress}
            label={`${uploadProgress}%`}
            className="progress-lg"
            style={{ minHeight: "2rem" }}
          />
        )}
        <p className="text-body-secondary my-0">
          <small>
            <em>
              <FontAwesomeIcon icon={faCircleInfo} className="text-info" />{" "}
              {libraryCipherNote}
            </em>
          </small>
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          disabled={isUploading}
          onClick={() => setShow(false)}
        >
          Cancel
        </Button>
        <Button
          variant="dark"
          disabled={isUploading || !file}
          onClick={handleReplace}
        >
          Replace
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
